import { IonModal } from "@ionic/react";
import type * as React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { sheetAnimations } from "@/lib/modal-animations";

export interface SheetRequest {
  /** Called once the sheet has closed, after any pick has landed. */
  onClosed: () => void;
  /**
   * Called with the row picked: once the sheet has closed, or at a press
   * outside it while it leaves, whichever comes first.
   */
  onPick: (pick: string) => void;
  title: string;
}

interface PickerSheetHandle {
  /**
   * Closes the request's sheet; `pick` is delivered once it has closed, or at
   * a press outside it while it leaves.
   */
  close: (request: SheetRequest, pick: string | null) => void;
  /** Shows the sheet; refused while another is still open or closing. */
  open: (request: SheetRequest) => boolean;
}

const SHEET_CLASS = "picker-sheet";

/** Marks the field the sheet focuses once it has presented. */
export const AUTOFOCUS_ATTRIBUTE = "data-sheet-autofocus";

const PickerSheetContext = createContext<PickerSheetHandle | null>(null);
/** Where the open picker portals its list; `null` while nothing is shown. */
const PickerSheetContainerContext = createContext<HTMLElement | null>(null);

/**
 * The one sheet every picker on a touch screen opens. It lives above every
 * screen, so a picker that leaves the tree while its sheet is up (its own pick
 * swaps the view, a synced write hides its field) only closes it: an
 * `ion-modal` unmounted while presented leaves the page aria-hidden and
 * scroll-locked. The picker portals its list into the sheet, and the sheet
 * holds the pick, so it lands even when the picker is gone.
 */
export function PickerSheetProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [shown, setShown] = useState<SheetRequest | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const current = useRef<SheetRequest | null>(null);
  const pick = useRef<string | null>(null);
  // Once `ion-modal` has seen isOpen, Ionic presents and then dismisses it,
  // so only its didDismiss may end the request; before that, nothing will.
  const modalSawOpen = useRef(false);
  useLayoutEffect(() => {
    if (isOpen) {
      modalSawOpen.current = true;
    }
  }, [isOpen]);

  // A leaving sheet's backdrop lets presses through, so a press on the page
  // behind it (a Save) must find the pick already landed: at its click, in
  // the capture phase, whose target a dialog the pick opens cannot take.
  useLayoutEffect(() => {
    if (!shown || isOpen) {
      return;
    }
    const landPick = (event: MouseEvent) => {
      const picked = pick.current;
      const inSheet =
        event.target instanceof Element &&
        event.target.closest(`.${SHEET_CLASS}`) !== null;
      if (picked === null || inSheet) {
        return;
      }
      pick.current = null;
      shown.onPick(picked);
    };
    document.addEventListener("click", landPick, true);
    return () => document.removeEventListener("click", landPick, true);
  }, [isOpen, shown]);

  const finish = useCallback(() => {
    const closed = current.current;
    if (!closed) {
      return;
    }
    const picked = pick.current;
    current.current = null;
    pick.current = null;
    modalSawOpen.current = false;
    setIsOpen(false);
    setShown(null);
    // Outside React's commit, so a handler that throws is an error in that
    // handler, not one that takes the app's root down; and after the closing
    // render, so a confirm dialog it opens opens on a settled page.
    setTimeout(() => {
      try {
        if (picked !== null) {
          closed.onPick(picked);
        }
      } finally {
        closed.onClosed();
      }
    }, 0);
  }, []);

  const open = useCallback((request: SheetRequest) => {
    if (current.current) {
      return false;
    }
    current.current = request;
    pick.current = null;
    setShown(request);
    setIsOpen(true);
    return true;
  }, []);

  const close = useCallback(
    (request: SheetRequest, picked: string | null) => {
      if (current.current !== request) {
        return;
      }
      if (picked !== null) {
        pick.current = picked;
      }
      if (modalSawOpen.current) {
        setIsOpen(false);
      } else {
        finish();
      }
    },
    [finish]
  );

  const focusSearch = useCallback(() => {
    container?.querySelector<HTMLElement>(`[${AUTOFOCUS_ATTRIBUTE}]`)?.focus();
  }, [container]);

  const handle = useMemo(() => ({ close, open }), [close, open]);

  return (
    <PickerSheetContext.Provider value={handle}>
      <PickerSheetContainerContext.Provider value={container}>
        {children}
      </PickerSheetContainerContext.Provider>
      <IonModal
        aria-label={shown?.title}
        className={SHEET_CLASS}
        enterAnimation={sheetAnimations.enterAnimation}
        isOpen={isOpen}
        // Ionic blurs a focused input as a modal enters and then focuses the
        // modal itself, so an asked-for search field is focused once it is in.
        keyboardClose={false}
        leaveAnimation={sheetAnimations.leaveAnimation}
        onDidDismiss={finish}
        onDidPresent={focusSearch}
      >
        <div ref={setContainer} />
      </IonModal>
    </PickerSheetContext.Provider>
  );
}

/** `null` outside the provider, where only the popover can open. */
export function usePickerSheet(): PickerSheetHandle | null {
  return useContext(PickerSheetContext);
}

export function usePickerSheetContainer(): HTMLElement | null {
  return useContext(PickerSheetContainerContext);
}
